/**
 * TERMS AND CONDITIONS PAGE
 * Legal terms for SkillProof Assessment Platform
 * 8ANDH ENGINEERING SERVICES PRIVATE LIMITED (We Remote Devs)
 */

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TermsConditions = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 17, 2026";

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-8 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card>
          <CardContent className="p-4 sm:p-8 prose prose-sm max-w-none break-words">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-8 h-8 text-purple-600" />
              <h1 className="text-2xl sm:text-3xl font-bold m-0">Terms and Conditions</h1>
            </div>

            <p className="text-gray-500 mb-8">Last Updated: {lastUpdated}</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">1. AGREEMENT TO TERMS</h2>
              <p>
                These Terms and Conditions ("Terms," "Agreement") constitute a legally binding agreement 
                between you ("Candidate," "User," "you," or "your") and 8ANDH ENGINEERING SERVICES PRIVATE 
                LIMITED, a company incorporated under the Companies Act, 2013, having its registered office 
                at Q5H4, Bhartiya City, Kannuru, Bangalore, Karnataka 560064, India, operating under the 
                trade names "We Remote Devs" and "SkillProof" ("Company," "we," "us," or "our").
              </p>
              <p>
                BY ACCESSING, REGISTERING FOR, OR USING THE SKILLPROOF TECHNICAL ASSESSMENT PLATFORM 
                (THE "PLATFORM" OR "SERVICES"), YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE 
                TO BE BOUND BY THESE TERMS IN THEIR ENTIRETY. IF YOU DO NOT AGREE TO ALL OF THESE TERMS, 
                YOU ARE EXPRESSLY PROHIBITED FROM USING THE PLATFORM AND MUST DISCONTINUE USE IMMEDIATELY.
              </p>
              <p>
                These Terms incorporate by reference our Privacy Policy, which is available at 
                test.weremotedevs.com/privacy-policy. Your use of the Platform constitutes acceptance 
                of both these Terms and the Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2. DEFINITIONS AND INTERPRETATION</h2>
              <p>For the purposes of these Terms:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>"Assessment"</strong> means any technical evaluation, test, examination, or 
                skill verification conducted through the Platform.</li>
                <li><strong>"Assessment Report"</strong> means the document generated upon completion of 
                an Assessment, containing scores, evaluations, and related information.</li>
                <li><strong>"Client"</strong> means any organization or individual that engages the Company 
                to conduct Assessments of Candidates.</li>
                <li><strong>"Intellectual Property"</strong> means all patents, copyrights, trademarks, 
                trade secrets, and other proprietary rights.</li>
                <li><strong>"Proctoring"</strong> means the monitoring and supervision of Assessments 
                through technological means including webcam, microphone, and screen monitoring.</li>
                <li><strong>"Prohibited Conduct"</strong> has the meaning ascribed in Section 7.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3. ELIGIBILITY AND REGISTRATION</h2>
              
              <h3 className="text-lg font-medium mt-4 mb-2">3.1 Eligibility Requirements</h3>
              <p>To use the Platform, you must:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Be at least eighteen (18) years of age or the age of legal majority in your jurisdiction;</li>
                <li>Have the legal capacity to enter into binding contracts;</li>
                <li>Not be prohibited from using the Platform under applicable laws;</li>
                <li>Have received a valid assessment invitation from the Company or a Client.</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">3.2 Account Credentials</h3>
              <p>
                You are solely responsible for maintaining the confidentiality of any authentication 
                credentials, including one-time passwords (OTPs) and unique access tokens. You agree 
                to immediately notify the Company of any unauthorized use of your credentials or any 
                other breach of security. The Company shall not be liable for any loss or damage arising 
                from unauthorized use of your credentials.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">3.3 Accuracy of Information</h3>
              <p>
                You represent and warrant that all information provided to the Company is accurate, 
                current, and complete. You agree to update such information as necessary to maintain 
                its accuracy. Provision of false, misleading, or fraudulent information constitutes 
                a material breach of these Terms and may result in immediate termination and legal action.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">4. ASSESSMENT SERVICES</h2>
              
              <h3 className="text-lg font-medium mt-4 mb-2">4.1 Nature of Services</h3>
              <p>
                The Platform provides AI-powered technical assessments designed to evaluate candidates' 
                skills, knowledge, and competencies. Assessments may include, but are not limited to, 
                multiple-choice questions, coding challenges, fill-in-the-blank questions, essay responses, 
                and system design evaluations.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">4.2 Assessment Parameters</h3>
              <p>
                Each Assessment is subject to specific parameters including time limits, question counts, 
                passing thresholds, and permitted resources. These parameters are determined by the Client 
                and/or the Company and are communicated to you prior to commencement. Failure to complete 
                an Assessment within the allotted time will result in automatic submission of completed answers.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">4.3 Technical Requirements</h3>
              <p>You acknowledge and agree that successful completion of an Assessment requires:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>A computer with a functional webcam and microphone;</li>
                <li>A stable internet connection with minimum 1 Mbps bandwidth;</li>
                <li>A modern web browser (Chrome, Firefox, Safari, or Edge) with JavaScript enabled;</li>
                <li>A quiet, well-lit environment suitable for proctored examination;</li>
                <li>Valid government-issued identification (if requested).</li>
              </ul>
              <p className="mt-2">
                The Company is not responsible for technical failures on your end, including but not 
                limited to internet connectivity issues, hardware malfunctions, or incompatible software.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">5. PROCTORING AND MONITORING</h2>
              
              <h3 className="text-lg font-medium mt-4 mb-2">5.1 Consent to Monitoring</h3>
              <p className="font-medium text-red-700">
                BY COMMENCING AN ASSESSMENT, YOU EXPRESSLY AND UNEQUIVOCALLY CONSENT TO COMPREHENSIVE 
                MONITORING AND RECORDING OF YOUR ASSESSMENT SESSION, INCLUDING:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Continuous webcam video capture and periodic photograph captures;</li>
                <li>Audio recording through your device microphone;</li>
                <li>Screen activity monitoring including application usage and browser tabs;</li>
                <li>Detection and recording of tab switches, window focus changes, and navigation attempts;</li>
                <li>Copy, paste, and clipboard monitoring;</li>
                <li>Facial recognition and presence detection using artificial intelligence;</li>
                <li>Detection of additional persons, mobile phones, or prohibited objects;</li>
                <li>Voice activity detection and analysis;</li>
                <li>Keystroke patterns and behavioral biometrics;</li>
                <li>Network and browser environment analysis.</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">5.2 Purpose of Proctoring</h3>
              <p>
                Proctoring measures are implemented to maintain the integrity and validity of Assessments, 
                detect and deter cheating or fraudulent conduct, verify candidate identity, and ensure 
                fair evaluation of all candidates. You acknowledge that proctoring data may be reviewed 
                by authorized personnel and may be used as evidence in case of suspected violations.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">5.3 Integrity Score</h3>
              <p>
                Your Assessment session will be assigned an integrity score based on detected violations 
                and anomalies. Violations may result in point deductions, warnings, or immediate termination 
                of the Assessment. The Company reserves the right to invalidate Assessment results if 
                integrity violations exceed acceptable thresholds.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">6. ARTIFICIAL INTELLIGENCE DISCLOSURE</h2>
              <p>
                You acknowledge and consent to the use of artificial intelligence and machine learning 
                technologies throughout the Assessment process, including:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Automated generation of assessment questions;</li>
                <li>Evaluation and scoring of responses including code submissions;</li>
                <li>Generation of assessment reports and recommendations;</li>
                <li>Real-time proctoring and anomaly detection;</li>
                <li>Analysis of written responses for quality and accuracy.</li>
              </ul>
              <p className="mt-4">
                AI-powered evaluations are provided "as is" and may contain errors, inconsistencies, or 
                biases. The Company makes no warranties regarding the accuracy or reliability of AI-generated 
                assessments. Final employment decisions are made solely by the Client, and the Company 
                disclaims all liability for hiring outcomes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">7. PROHIBITED CONDUCT</h2>
              <p>
                You agree that you will NOT engage in any of the following prohibited conduct 
                ("Prohibited Conduct") during an Assessment:
              </p>
              
              <h3 className="text-lg font-medium mt-4 mb-2">7.1 Academic Dishonesty</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Receiving assistance from any person, whether in-person or remotely;</li>
                <li>Using unauthorized materials, notes, books, or reference documents;</li>
                <li>Accessing external websites, applications, or resources not expressly permitted;</li>
                <li>Using artificial intelligence tools (ChatGPT, Copilot, etc.) to generate answers;</li>
                <li>Copying, sharing, or distributing Assessment content;</li>
                <li>Having another person complete the Assessment on your behalf (impersonation);</li>
                <li>Communicating with others during the Assessment through any means.</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">7.2 Technical Violations</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Disabling, obstructing, or circumventing proctoring features;</li>
                <li>Using multiple devices, browsers, or browser windows;</li>
                <li>Using virtual machines, screen sharing, or remote desktop software;</li>
                <li>Manipulating network traffic or attempting to intercept data;</li>
                <li>Attempting to access, modify, or exploit platform vulnerabilities.</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">7.3 Environmental Violations</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Having additional persons present in the assessment area;</li>
                <li>Using mobile phones or secondary devices;</li>
                <li>Leaving the webcam view during the Assessment;</li>
                <li>Obscuring your face or identity from the webcam;</li>
                <li>Creating environmental conditions that impair proctoring effectiveness.</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">7.4 Consequences of Violations</h3>
              <p>
                Engagement in Prohibited Conduct may result in one or more of the following consequences, 
                at the Company's sole discretion:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Immediate termination of the Assessment session;</li>
                <li>Invalidation of Assessment results;</li>
                <li>Notification to the Client of the violation;</li>
                <li>Permanent ban from the Platform;</li>
                <li>Reporting to relevant professional bodies or authorities;</li>
                <li>Legal action for damages, including but not limited to breach of contract and fraud.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">8. INTELLECTUAL PROPERTY RIGHTS</h2>
              
              <h3 className="text-lg font-medium mt-4 mb-2">8.1 Company Intellectual Property</h3>
              <p>
                The Platform, including all software, algorithms, user interfaces, content, questions, 
                methodologies, and documentation, is the exclusive property of the Company and is protected 
                by copyright, trademark, patent, trade secret, and other intellectual property laws. You 
                are granted a limited, non-exclusive, non-transferable, revocable license to access and 
                use the Platform solely for the purpose of completing authorized Assessments.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">8.2 Assessment Content</h3>
              <p>
                All Assessment questions, problems, code snippets, and related materials are confidential 
                and proprietary. You agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Copy, reproduce, or record Assessment content by any means;</li>
                <li>Share, distribute, or publish Assessment content;</li>
                <li>Discuss specific Assessment questions with third parties;</li>
                <li>Use Assessment content for any purpose other than completing the Assessment.</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">8.3 Your Submissions</h3>
              <p>
                By submitting responses, code, or other content through the Platform, you grant the Company 
                a worldwide, perpetual, irrevocable, royalty-free license to use, reproduce, modify, analyze, 
                and display such content for purposes including evaluation, quality assurance, platform 
                improvement, and anonymized research. You retain ownership of original work, subject to this license.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">9. DISCLAIMERS AND LIMITATION OF LIABILITY</h2>
              
              <h3 className="text-lg font-medium mt-4 mb-2">9.1 Disclaimer of Warranties</h3>
              <p className="uppercase font-medium">
                THE PLATFORM AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF 
                ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND TITLE. THE COMPANY 
                DOES NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF 
                VIRUSES OR OTHER HARMFUL COMPONENTS.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">9.2 Limitation of Liability</h3>
              <p className="uppercase font-medium">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE COMPANY, ITS 
                DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, 
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT 
                LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, 
                ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF OR INABILITY TO USE THE PLATFORM, 
                REGARDLESS OF THE THEORY OF LIABILITY AND EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">9.3 Maximum Liability</h3>
              <p>
                In no event shall the Company's total cumulative liability to you for all claims arising 
                out of or relating to these Terms or the Platform exceed the greater of (a) the amount 
                paid by you, if any, for accessing the Platform, or (b) One Hundred Indian Rupees (₹100).
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">9.4 Employment Outcomes</h3>
              <p>
                The Company expressly disclaims any and all liability for employment decisions made by 
                Clients based on Assessment results. The Company does not guarantee employment, interviews, 
                or any specific outcome as a result of completing an Assessment. Hiring decisions are made 
                solely by Clients at their independent discretion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">10. INDEMNIFICATION</h2>
              <p>
                You agree to indemnify, defend, and hold harmless the Company and its officers, directors, 
                employees, agents, licensors, and suppliers from and against any and all claims, liabilities, 
                damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of 
                or in any way connected with:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your access to or use of the Platform;</li>
                <li>Your violation of these Terms;</li>
                <li>Your violation of any third-party rights, including intellectual property rights;</li>
                <li>Your engagement in Prohibited Conduct;</li>
                <li>Any content or information you submit through the Platform;</li>
                <li>Any claim by a third party arising from your actions or omissions.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">11. TERMINATION</h2>
              <p>
                The Company may terminate or suspend your access to the Platform immediately, without 
                prior notice or liability, for any reason whatsoever, including but not limited to breach 
                of these Terms. Upon termination, your right to use the Platform will cease immediately. 
                All provisions of these Terms which by their nature should survive termination shall survive, 
                including ownership provisions, warranty disclaimers, indemnification, and limitations of liability.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">12. DISPUTE RESOLUTION</h2>
              
              <h3 className="text-lg font-medium mt-4 mb-2">12.1 Governing Law</h3>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of India, 
                without regard to its conflict of law provisions.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">12.2 Arbitration</h3>
              <p>
                Any dispute, controversy, or claim arising out of or relating to these Terms or the 
                breach, termination, or validity thereof shall be settled by binding arbitration in 
                accordance with the Arbitration and Conciliation Act, 1996, as amended. The arbitration 
                shall be conducted by a sole arbitrator appointed by mutual agreement, or failing such 
                agreement, in accordance with the said Act. The seat of arbitration shall be Bangalore, 
                Karnataka, India. The language of the arbitration shall be English.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">12.3 Exclusive Jurisdiction</h3>
              <p>
                Subject to the arbitration clause above, the courts of Bangalore, Karnataka, India 
                shall have exclusive jurisdiction over any disputes arising under these Terms.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">12.4 Class Action Waiver</h3>
              <p className="font-medium">
                YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL 
                BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION. You waive any right to 
                participate in any class action or class-wide arbitration.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">13. MISCELLANEOUS PROVISIONS</h2>
              
              <h3 className="text-lg font-medium mt-4 mb-2">13.1 Entire Agreement</h3>
              <p>
                These Terms, together with the Privacy Policy, constitute the entire agreement between 
                you and the Company regarding your use of the Platform and supersede all prior agreements 
                and understandings.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">13.2 Severability</h3>
              <p>
                If any provision of these Terms is held to be invalid, illegal, or unenforceable, such 
                provision shall be modified to the minimum extent necessary to make it valid, legal, 
                and enforceable, and the remaining provisions shall continue in full force and effect.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">13.3 Waiver</h3>
              <p>
                The failure of the Company to enforce any right or provision of these Terms shall not 
                constitute a waiver of such right or provision. Any waiver must be in writing and signed 
                by an authorized representative of the Company.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">13.4 Assignment</h3>
              <p>
                You may not assign or transfer your rights or obligations under these Terms without 
                the prior written consent of the Company. The Company may assign its rights and obligations 
                without restriction.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">13.5 Amendments</h3>
              <p>
                The Company reserves the right to modify these Terms at any time. Changes will be effective 
                upon posting to the Platform. Your continued use of the Platform following any changes 
                constitutes acceptance of the modified Terms.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">13.6 Force Majeure</h3>
              <p>
                The Company shall not be liable for any failure or delay in performance resulting from 
                causes beyond its reasonable control, including but not limited to acts of God, natural 
                disasters, war, terrorism, riots, labor disputes, governmental actions, or failures of 
                third-party services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">14. CONTACT INFORMATION</h2>
              <p>For any questions regarding these Terms, please contact:</p>
              <div className="bg-gray-100 p-4 rounded-lg my-4">
                <p className="font-semibold mb-1">8ANDH ENGINEERING SERVICES PRIVATE LIMITED</p>
                <p className="mb-1">Q5H4, Bhartiya City, Kannuru</p>
                <p className="mb-1">Bangalore, Karnataka 560064, India</p>
                <p>Email: business@weremotedevs.com</p>
              </div>
            </section>

            <section className="mb-8 bg-yellow-50 p-6 rounded-lg border border-yellow-200">
              <h2 className="text-xl font-semibold mb-4">ACKNOWLEDGMENT</h2>
              <p className="font-medium">
                BY CLICKING "I ACCEPT" OR BY COMMENCING AN ASSESSMENT, YOU ACKNOWLEDGE THAT YOU HAVE READ, 
                UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS AND THE PRIVACY POLICY. 
                YOU FURTHER ACKNOWLEDGE THAT YOU CONSENT TO THE COLLECTION AND PROCESSING OF YOUR PERSONAL 
                DATA, INCLUDING PROCTORING DATA, AS DESCRIBED IN THE PRIVACY POLICY.
              </p>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-500 text-center">
                © {new Date().getFullYear()} 8ANDH ENGINEERING SERVICES PRIVATE LIMITED. All rights reserved.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsConditions;
